import { UserFieldResponse } from '../field/user-field-response';
import { FieldType } from '../field/field';
import { dateRangeDelimiter, defaultDateTimeRangeFormat } from 'src/constants/defaults';
import * as fp from 'lodash/fp';
import { reformatDateValue, formatDateValue } from 'src/shared/utils/date-utils';

export class CreateRequestApplicationModel {

	static fromAny(data: any) {
		const ufr = (data.userFieldResponses || []).map((r: any) => UserFieldResponse.fromAny(r));
		return new CreateRequestApplicationModel(data.title, ufr, data.description);
	}

	static equals(a: any, b: any) {
		if (!a || !b) return false;
		return fp.isEqual(CreateRequestApplicationModel.fromAny(a), CreateRequestApplicationModel.fromAny(b));
	}

	constructor(
		public title: string,
		public userFieldResponses: Array<UserFieldResponse>,
		public fieldGroupId: string,
		public fieldsValue?: string,
	) { }

	toDto(format?: string) {
		return new CreateRequestApplicationModel(
			this.title,
			this.userFieldResponses.map(ufr => ufr.toDto()),
			this.fieldGroupId,
			this.userFieldResponses.map(ufr => this.formatDate(ufr, format)).join('')
		);
	}

	formatDate(ufr: UserFieldResponse, format?: string): string {
		const fieldType = ufr.meta?.field?.type;

		// Handle DateRange separately
		if (fieldType === FieldType.DateRange && typeof ufr.response === 'string' && ufr.response.includes(dateRangeDelimiter)) {
			const [vonDate, bisDate] = ufr.response.split(dateRangeDelimiter);

			const vonFormatted = format ? reformatDateValue(vonDate, format, format) : vonDate;
			const bisFormatted = format ? reformatDateValue(bisDate, format, format) : bisDate;

			const [vonFieldName, bisFieldName] = (ufr.meta?.field?.name ?? 'Von' + dateRangeDelimiter + 'Bis')
				.split(dateRangeDelimiter)
				.map(part => part.trim());

			return `"${vonFieldName}"="${vonFormatted}";"${bisFieldName}"="${bisFormatted}";`;
		}

		// Handle DateTimeRange separately
		if (fieldType === FieldType.DateTimeRange && typeof ufr.response === 'string' && ufr.response.includes(dateRangeDelimiter)) {
			const [vonDate, bisDate] = ufr.response.split(dateRangeDelimiter);

			const vonFormatted = format ? reformatDateValue(vonDate, defaultDateTimeRangeFormat, format) : vonDate;
			const bisFormatted = format ? reformatDateValue(bisDate, defaultDateTimeRangeFormat, format) : bisDate;

			const [vonFieldName, bisFieldName] = (ufr.meta?.field?.name ?? 'Von' + dateRangeDelimiter + 'Bis')
				.split(dateRangeDelimiter)
				.map(part => part.trim());

			return `"${vonFieldName}"="${vonFormatted}";"${bisFieldName}"="${bisFormatted}";`;
		}

		// Handle DateTime formatting
		const newUfr: UserFieldResponse = {
			fieldId: ufr.fieldId,
			response: fieldType === FieldType.DateTime
				? this.toAbsoluteDateTime(ufr.response)
				: ufr.response,
			meta: ufr.meta,
			formattedResult: ufr.formattedResult,
			toDto: ufr.toDto.bind(ufr),
			toUpdateRequestDto: ufr.toUpdateRequestDto.bind(ufr),
			toFieldsValue: ufr.toFieldsValue.bind(ufr)
		};

		if (fieldType === FieldType.DateTime) {
			const formattedDate = formatDateValue(newUfr.response, format);
			newUfr.response = formattedDate;
		}

		return newUfr.toFieldsValue(newUfr.response);
	}

	toAbsoluteDateTime(dateTime: string) {
		if (dateTime.includes("Z"))
			dateTime = dateTime.substring(0, dateTime.length - (dateTime.length - dateTime.indexOf("Z")));
		if (dateTime.includes("+"))
			dateTime = dateTime.substring(0, dateTime.length - (dateTime.length - dateTime.indexOf("+")));
		return dateTime;
	}
}